import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { EnvRoleService } from '../services/env-role.service';

/**
 * envRoleInterceptor — service-layer enforcement of environment isolation.
 *
 * This interceptor is the SECOND layer of defense (the first being route guards
 * and component-level isAdmin/isAnalyst checks).
 *
 * For every outgoing request to /api/*:
 *   • It checks whether the authenticated user holds at least one role that is
 *     valid for the DEPLOYED environment (via EnvRoleService.hasEnvRoles).
 *   • If not, it short-circuits the request and returns a 403 HttpErrorResponse
 *     — no network call is made and no data crosses environments.
 *
 * Non-API URLs (static assets, /userdetails, external calls) pass through
 * unconditionally.
 *
 * Placement in the interceptor chain (app.config.ts):
 *   requestHeadersInterceptor → envRoleInterceptor → chassisResponseInterceptor
 *
 * This order ensures env validation happens BEFORE the chassis envelope is
 * unwrapped, keeping the error surfaced cleanly.
 */
export const envRoleInterceptor: HttpInterceptorFn = (request, next) => {
  // Only intercept backend API calls — pass everything else through
  if (!request.url.startsWith('/api')) {
    return next(request);
  }

  const envRoleService = inject(EnvRoleService);

  if (!envRoleService.hasEnvRoles()) {
    const env = envRoleService.deployedEnv;
    return throwError(() =>
      new HttpErrorResponse({
        status: 403,
        statusText: 'Environment Access Denied',
        error: {
          message:
            `You do not have a valid role for the ${env} environment. ` +
            `Please contact your administrator if you believe this is an error.`
        },
        url: request.url
      })
    );
  }

  return next(request);
};

